import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { createMsalInstance } from "@timeghost/auth/msal-instance";
import { AppInitializer } from "@timeghost/auth/react/app-initializer";
import React from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import ErrorPage from "@/components/pages/error-page";
import SpinnerPage from "@/components/pages/spinner-page";
import Toaster from "@/components/toaster";
import { environment } from "./lib/environment";
import i18n from "./lib/i18n";
import { cachePreAuthRoute, getTeamsLoginHint, notifyTeamsAppLoaded, resolveAuthHost } from "./lib/teams/host";
import { resolveTeamsSsoAccount } from "./lib/teams/teams-sso";
import { initializeTheme, startSystemThemeListener } from "./lib/theme";
import { router } from "./router";
import "./styles.css";

/** Fresh window: skip focus refetch until stale. 3m fits timer/feed; SignalR + manual refresh still win. */
const QUERY_STALE_TIME_MS = 3 * 60 * 1000;

const queryClient: QueryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: QUERY_STALE_TIME_MS,
			refetchOnWindowFocus: true,
		},
	},
});
const msalInstance = createMsalInstance({
	VITE_AZURE_AUTHORITY: environment.VITE_AZURE_AUTHORITY,
	VITE_AZURE_CLIENT_ID: environment.VITE_AZURE_CLIENT_ID,
	VITE_AZURE_REDIRECT_URI: environment.VITE_AZURE_REDIRECT_URI,
	VITE_AZURE_POST_LOGOUT_REDIRECT_URI: environment.VITE_AZURE_POST_LOGOUT_REDIRECT_URI,
});

const rootElement: HTMLElement | null = document.getElementById("root");

if (rootElement === null) {
	throw new Error("Root element not found");
}

initializeTheme();
startSystemThemeListener();
if (!window.location.search.includes("iframe"))
	ReactDOM.createRoot(rootElement).render(
		<React.StrictMode>
			<MsalProvider instance={msalInstance}>
				<AppInitializer
					configuredScopes={environment.VITE_AZURE_SCOPES}
					error={<ErrorPage />}
					getTeamsLoginHint={getTeamsLoginHint}
					initializing={<SpinnerPage />}
					onBeforeInteractive={() => {
						cachePreAuthRoute();
					}}
					onGuest={() => {
						void notifyTeamsAppLoaded();
					}}
					onReady={() => {
						void notifyTeamsAppLoaded();
					}}
					resolveHost={resolveAuthHost}
					resolveHostAccount={resolveTeamsSsoAccount}>
					<I18nextProvider i18n={i18n}>
						<QueryClientProvider client={queryClient}>
							<RouterProvider router={router} />
							<Toaster expand position="bottom-center" />
						</QueryClientProvider>
					</I18nextProvider>
				</AppInitializer>
			</MsalProvider>
		</React.StrictMode>
	);
